<template>
    <div class="grid" v-if="!hasServerError">
        <div class="grid-item">
            <div class="tile">
                <div class="tile-content tile-content-segment">
                    <segment-button
                        v-for="(activeSegment, activeSegmentIndex) in activeSegments"
                        :segment="activeSegment"
                        @toggle-visiblity="toggleSegment"
                        @deactivate="deactivateSegment"
                        @click="toggleEditSegment"
                        :editing="editingSegment"
                        :index="activeSegmentIndex"
                        :key="activeSegmentIndex">
                        {{activeSegment.name}}
                    </segment-button>
                    <v-popper2 :state="showAddSegmentPopper" v-if="!hasMaxActiveSegments && canAddSegment">
                        <template #trigger="{popper}">
                            <v-icon-button data-cy="Add segment button" @click="popper.toggle">
                                Add Segment
                                <v-icon icon="caret-down" slot="right-icon"/>
                            </v-icon-button>
                        </template>
                        <template #content>
                            <div class="dropdown">
                                <div class="dropdown-content dropdown-content-padded" :class="{'is-scrollable': allSegments.length > 6}">
                                    <ul class="list list-group">
                                        <li class="list-item" v-for="(segment, segmentIndex) in allSegments" :key="segment.id">
                                            <v-checkbox
                                                :label="segment.name"
                                                :name="segment.id"
                                                :value="segment.id"
                                                v-model="tempActiveSegments"
                                                :disabled="!tempActiveSegments.includes(segment.id) && tempActiveSegments.length >= 3"
                                            />
                                        </li>
                                    </ul>
                                </div>
                                <div class="dropdown-content dropdown-content-padded">
                                    <r-button size="small" :fluid="true" class="stack-s" @click="applyTemptActiveSegments">Apply Selection</r-button>
                                        <div class="divider has-or stack-s">&nbsp;&nbsp;or&nbsp;&nbsp;</div>
                                    <r-button size="small" type="primary" :fluid="true" @click="createNewSegment">Create New Segment</r-button>
                                </div>
                            </div>
                        </template>
                    </v-popper2>
                </div>
                <div v-show="isModifyingSegment">
                    <div class="tile-content tile-content-secondary">
                        <div class="grid-content">
                            <div class="grid-item grid-item-half">
                                <r-input left-label="Segment Name" v-model="editingSegment.name"/>
                            </div>
                        </div>
                    </div>
                    <div class="tile-content tile-content-secondary tile-content-criteria">
                        <query
                            type="filter"
                            :query="editingSegment.filter"
                            :options="criteriaOptions"
                            @ready="filterReady"
                            @update="updateCriteria"/>
                    </div>
                    <div class="tile-content tile-content-secondary equal-widths-elements-mobile">
                        <r-button type="primary" class="inline-s" @click="saveSegment">Save Segment</r-button>
                        <r-button @click="cancelEditing">Cancel</r-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import vPopper2 from '@/components/popper/v-popper2';
    import vIcon from '@/components/form/v-icon';
    import vCheckbox from '@/components/form/v-checkbox';
    import vIconButton from '@/components/form/v-icon-button';
    import DashboardBus from './dashboard-bus';
    import segmentsMixins from './segments-mixins';
    import SegmentButton from './segment-button';
    import segmentFilters from './segment-filters';
    import Segment from './segment';
    import PreferenceTypes from '@/store/preference-types';

    /**
     * Segment Editor
     */
    export default {
        mixins: [segmentsMixins],
        name: 'segments',
        components: {
            SegmentButton,
            Query: () => import('@/components/query/query'),
            vPopper2,
            vIcon,
            vIconButton,
            vCheckbox,
        },
        computed: {
            hasMaxActiveSegments() {
                if (this.preferences) {
                    return this.preferences.activeSegments.length >= this.maximumActiveSegmentsCount;
                }
                return false;
            },
            allSegments() {
                if (this.preferences) {
                    this.tempActiveSegments = (this.preferences.activeSegments || []).slice();
                    return Object.keys(this.preferences.segments)
                        .map(id => this.preferences.segments[id]);
                }
                return [];
            },
            canAddSegment() {
                return !this.isModifyingSegment;
            },
        },
        data() {
            return {
                componentId: 'dashboard/segments',
                criteriaOptions: {
                    filterSet: segmentFilters,
                },
                isModifyingSegment: false,
                editingSegment: new Segment(),
                maximumActiveSegmentsCount: 3,
                tempActiveSegments: [],
                showAddSegmentPopper: null,
            };
        },
        methods: {
            /**
             * Set up a default segment with no filter if none are pulled from the user's preferences.
             *
             * @public
             * @returns {Promise<void>}
             */
            async setupDefaultSegment() {
                const defaultSegment = new Segment({name: 'All'});
                const data = {
                    segments: {...defaultSegment.getNormalized()},
                    activeSegments: [defaultSegment.id],
                };
                this.setUserPreference({type: PreferenceTypes.modules, id: this.componentId, data});
            },
            updateCriteria(criteria) {
                this.editingSegment.filter = criteria;
            },
            /**
             * Trigger an API call to dashboard endpoint and re-draw the metrics.
             */
            filterReady() {
                // only draw and stop loading when the filters are ready
                DashboardBus.$emit('segments-updated');
                this.$stopLoading(this.componentId);
            },
            toggleSegment(segmentId) {
                this.$store.dispatch('dashboard/toggleSegment', {id: segmentId});
                if (!this.$store.getters['dashboard/isHidden'](segmentId)) {
                    DashboardBus.$emit('reflow-sparkline', segmentId);
                }
            },
            deactivateSegment(segmentId) {
                const preferences = this.$clonePlainObject(this.preferences);
                preferences.activeSegments = preferences.activeSegments.filter(id => id !== segmentId);
                // reset visibility
                this.$store.dispatch('dashboard/showSegment', {id: segmentId});
                this.cancelEditing();
                this.saveAndRefresh(preferences);
            },
            createNewSegment() {
                this.editingSegment = new Segment();
                this.isModifyingSegment = true;
            },
            /**
             * Save Segments edited on the Dashboard to the User's profile. This is called by
             * saveSegment.
             * @param data
             * @see saveSegment
             * @public
             */
            saveAndRefresh(data) {
                this.setUserPreference({type: PreferenceTypes.modules, id: this.componentId, data});
                DashboardBus.$emit('segments-updated');
            },
            /**
             * Called when a Segment is saved by the user. Stores in User's profile remotely.
             * @public
             */
            saveSegment() {
                if (this.editingSegment.name === null) {
                    this.$toast.negative('Unable to save segment: no name specified.');
                    return;
                }

                const segment = this.editingSegment;
                const preferences = this.$clonePlainObject(this.preferences);
                const isUpdating = segment.id in preferences.segments;
                const isActive = isUpdating && preferences.activeSegments.includes(segment.id);
                preferences.segments = {
                    ...preferences.segments,
                    ...segment.getNormalized(),
                };
                if (!isUpdating && !isActive && !this.hasMaxActiveSegments) {
                    preferences.activeSegments.push(segment.id);
                }
                this.saveAndRefresh(preferences);
                this.cancelEditing();
            },
            cancelEditing() {
                this.editingSegment = new Segment();
                this.isModifyingSegment = false;
            },
            toggleEditSegment(segmentId) {
                if (this.editingSegment.id === segmentId) {
                    this.cancelEditing();
                } else {
                    this.isModifyingSegment = true;
                    this.editingSegment = new Segment(this.preferences.segments[segmentId]);
                }
            },
            applyTemptActiveSegments() {
                const preferences = this.$clonePlainObject(this.preferences);
                preferences.activeSegments = [...this.tempActiveSegments];
                this.showAddSegmentPopper = false;
                this.$nextTick(() => { this.showAddSegmentPopper = null; });
                this.saveAndRefresh(preferences);
            },
        },
        created() {
            this.$startLoading(this.componentId);
            DashboardBus.$on('server-error', () => {
                // hide ui and show generic error message
                // whenever we get an unexpected server error
                this.hasServerError = true;
            });
        },
        mounted() {
            (async () => {
                if (!this.preferences) {
                    await this.setupDefaultSegment();
                }
            })();
        },
        beforeDestroy() {
            DashboardBus.$off('server-error');
            this.$stopLoading(this.componentId);
        },
    };
</script>
